import { TFunction } from 'i18next';
import * as XLSX from 'xlsx';
import {
  StudentMetricsDashboardContent,
  StudentMetricsDashboardFilters,
} from '@/school-management/types/generate-report';
import {
  getStudentsColumnsData,
  getAvgScoresDrillDownColumnsData,
  getAvgScoresDrillDownData,
  getStudentsDrillDownData,
} from '@/components/school-management/StudentMetricsDashboard/normalizeData';
import { UserState } from '@/types/user';

export interface XlsxTable {
  name: string;
  headerRow: string[];
  data: {
    [key: string]: any;
  }[];
}
export interface XlsxData {
  fileName: string;
  tables: XlsxTable[];
}

export interface Processing {
  createXlsx: (data: XlsxData) => void;
  exportStudentMetricsDashboard: (
    t: TFunction,
    data: StudentMetricsDashboardContent,
    filters: StudentMetricsDashboardFilters,
    schoolId: string,
    user: UserState | null
  ) => void;
}

const processing: Processing = {
  createXlsx: (data: XlsxData) => {
    const wb = XLSX.utils.book_new();

    data.tables.forEach(t => {
      const ws = XLSX.utils.json_to_sheet(t.data, {
        header: t.headerRow,
      });

      const wscols = t.headerRow.map(col => ({
        wch: col.length + 5,
      }));
      ws['!cols'] = wscols;

      XLSX.utils.book_append_sheet(wb, ws, t.name.slice(0, 31));
    });

    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    const dataBlob = new Blob([wbout], {
      type: 'application/octet-stream',
      //type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(dataBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  exportStudentMetricsDashboard: async (
    t: TFunction,
    data: StudentMetricsDashboardContent,
    filters: StudentMetricsDashboardFilters,
    schoolId: string,
    user: UserState | null
  ) => {
    const skillsData = (await getAvgScoresDrillDownData(filters)).data;
    const levelsData = (await getStudentsDrillDownData(filters, schoolId)).data || [];

    const xlsxLevelsColumns = getStudentsColumnsData(t, user).filter(c => !!c.xlsxRender);
    const xlsxSkillsColumns = getAvgScoresDrillDownColumnsData(t).filter(c => !!c.xlsxRender);

    const levelsHeaderRow = xlsxLevelsColumns.map(c => c.xlsxLabel || c.label);
    const skillsHeaderRow = xlsxSkillsColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: t('studentMetricsDashboard.exportData.fileName'),
      tables: [],
    };

    const levelsTable: XlsxTable = {
      name: t('studentMetricsDashboard.exportData.sheet.levels'),
      headerRow: levelsHeaderRow,
      data: [],
    };
    levelsData.forEach(l => {
      const row: any = {};
      xlsxLevelsColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      levelsTable.data.push(row);
    });
    xlsxData.tables.push(levelsTable);

    const skillTable: XlsxTable = {
      name: t('studentMetricsDashboard.exportData.sheet.skills'),
      headerRow: [...skillsHeaderRow],
      data: [],
    };
    skillsData.forEach(l => {
      const row: any = {};
      xlsxSkillsColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);

    processing.createXlsx(xlsxData);
  },
};

export default processing;
